import { type ContactEntry } from "../types";

interface Props {
  contacts: ContactEntry[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  onChange: (i: number, field: keyof ContactEntry, value: string) => void;
}

const CONTACT_TYPES = ["Téléphone", "Email", "WhatsApp", "Site web", "Facebook"];

const CONTACT_ICONS: Record<string, string> = {
  "Téléphone": "ri-phone-line",
  "Email": "ri-mail-line",
  "WhatsApp": "ri-whatsapp-line",
  "Site web": "ri-global-line",
  "Facebook": "ri-facebook-line",
};

const CONTACT_PLACEHOLDERS: Record<string, string> = {
  "Téléphone": "+221 33 821 04 56",
  "Email": "paroisse@diocese.sn",
  "WhatsApp": "+221 77 000 00 00",
  "Site web": "https://www.paroisse.sn",
  "Facebook": "https://facebook.com/paroisse",
};

export default function StepContacts({ contacts, onAdd, onRemove, onChange }: Props) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-stone-400 leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
        Ajoutez les coordonnées de contact de la paroisse — téléphone, email, réseaux sociaux, etc.
      </p>

      {/* Contact entries */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: "rgba(13,27,94,0.08)" }}>
              <i className="ri-contacts-line text-base" style={{ color: "#0D1B5E" }} />
            </div>
            <p className="font-bold text-stone-700 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
              Informations de contact
            </p>
          </div>
          <button
            type="button"
            onClick={onAdd}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
          >
            <i className="ri-add-line text-green-600 text-base" />
          </button>
        </div>

        <div className="px-4 py-3 flex flex-col gap-3">
          {contacts.map((contact, i) => (
            <div key={i} className="flex items-center gap-2">
              {/* Type */}
              <div className="relative w-36 flex-shrink-0">
                <select
                  value={contact.type}
                  onChange={(e) => onChange(i, "type", e.target.value)}
                  className="w-full pl-8 pr-2 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-xs text-stone-700 outline-none appearance-none cursor-pointer"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {CONTACT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                  <i className={`${CONTACT_ICONS[contact.type] || "ri-phone-line"} text-stone-400 text-sm`} />
                </div>
              </div>
              {/* Value */}
              <input
                type="text"
                value={contact.value}
                onChange={(e) => onChange(i, "value", e.target.value)}
                placeholder={CONTACT_PLACEHOLDERS[contact.type] || "Valeur..."}
                className="flex-1 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-xs text-stone-700 outline-none"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              />
              {/* Remove */}
              {contacts.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-red-50 cursor-pointer hover:bg-red-100 transition-colors flex-shrink-0"
                >
                  <i className="ri-delete-bin-line text-red-400 text-sm" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tip */}
      <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-lightbulb-line text-amber-500 text-sm" />
        </div>
        <p className="text-xs text-amber-700 leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Ajoutez au minimum un numéro de téléphone pour que les fidèles puissent contacter la paroisse.
        </p>
      </div>
    </div>
  );
}
